import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, ShoppingBag, User, Mail, Phone, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { DS } from "./VitrineShared";

const API = process.env.REACT_APP_BACKEND_URL;

export function InlineAuthModal({ open, onClose, onAuth, storeId, storeName, accentColor }) {
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [contact, setContact] = useState("");
  const [contactType, setContactType] = useState("email");
  const [password, setPassword] = useState("");

  const reset = () => { setFirstName(""); setContact(""); setPassword(""); setShowPwd(false); setMode("login"); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let res;
      if (mode === "register") {
        res = await axios.post(`${API}/api/customer/register`, {
          first_name: firstName, contact, contact_type: contactType, password, user_id: storeId,
        });
        toast.success("Bienvenue !");
      } else {
        res = await axios.post(`${API}/api/customer/login`, {
          contact, password, user_id: storeId,
        });
        toast.success("Connexion réussie !");
      }
      localStorage.setItem(`customer_token_${storeId}`, res.data.access_token);
      onAuth(res.data.access_token);
      reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  if (!open) return null;

  const ac = accentColor || DS.gold;

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(26,23,20,0.6)', zIndex:10000,
      display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)' }} data-testid="auth-modal-overlay">
      <div onClick={e => e.stopPropagation()} style={{ background:DS.white, borderRadius:'16px', width:'92%', maxWidth:'400px',
        padding:'28px 24px 24px', position:'relative', boxShadow:'0 24px 60px rgba(0,0,0,0.25)',
        animation:'vFadeUp 300ms ease-out both' }} data-testid="auth-modal">
        {/* Close */}
        <button onClick={onClose} style={{ position:'absolute', top:'14px', right:'14px', width:'28px', height:'28px',
          borderRadius:'50%', background:DS.sand, border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <X style={{ width:'14px', height:'14px', color:DS.ink }} />
        </button>

        {/* Title */}
        <div style={{ textAlign:'center', marginBottom:'20px' }}>
          <div style={{ width:'44px', height:'44px', borderRadius:'12px', background:`${ac}15`, border:`1px solid ${ac}30`,
            display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px', color:ac }}>
            <ShoppingBag style={{ width:'20px', height:'20px' }} />
          </div>
          <h2 className="vr-serif" style={{ fontSize:'22px', fontWeight:400, color:DS.ink, marginBottom:'4px' }}>Connexion requise</h2>
          <p style={{ fontFamily:'DM Sans,sans-serif', fontSize:'13px', color:DS.muted }}>
            {storeName ? `Connectez-vous à ${storeName}` : 'Connectez-vous pour commander'}
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', background:DS.cream, border:`1px solid ${DS.border}`, borderRadius:'10px', padding:'3px', marginBottom:'20px' }}
          data-testid="auth-modal-tabs">
          {[{key:'login', label:'Se connecter'}, {key:'register', label:'Créer un compte'}].map(t => (
            <button key={t.key} type="button" onClick={() => setMode(t.key)}
              style={{ flex:1, padding:'9px 0', border:'none', borderRadius:'8px', cursor:'pointer',
                background: mode === t.key ? DS.white : 'transparent',
                color: mode === t.key ? DS.ink : DS.muted,
                fontFamily:'DM Sans,sans-serif', fontSize:'13px', fontWeight: mode === t.key ? 600 : 400,
                boxShadow: mode === t.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none', transition:'all 200ms' }}
              data-testid={`auth-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
          {mode === 'register' && (
            <div>
              <label style={{ fontFamily:'DM Sans,sans-serif', fontSize:'11px', fontWeight:500, color:DS.muted,
                letterSpacing:'0.04em', textTransform:'uppercase', marginBottom:'5px', display:'block' }}>Prénom</label>
              <div style={{ position:'relative' }}>
                <User style={{ position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)', width:'15px', height:'15px', color:DS.stone }} />
                <input type="text" required value={firstName} onChange={e => setFirstName(e.target.value)}
                  placeholder="Marie" data-testid="auth-modal-firstname"
                  style={{ width:'100%', boxSizing:'border-box', padding:'11px 14px 11px 38px', borderRadius:'10px',
                    border:`1px solid ${DS.border}`, background:DS.cream, fontFamily:'DM Sans,sans-serif', fontSize:'14px', color:DS.ink, outline:'none' }} />
              </div>
            </div>
          )}
          {mode === 'register' && (
            <div>
              <label style={{ fontFamily:'DM Sans,sans-serif', fontSize:'11px', fontWeight:500, color:DS.muted,
                letterSpacing:'0.04em', textTransform:'uppercase', marginBottom:'5px', display:'block' }}>Me contacter par</label>
              <div style={{ display:'flex', gap:'8px' }}>
                {[{key:'email', icon: Mail, label:'Email'}, {key:'whatsapp', icon: Phone, label:'WhatsApp'}].map(ct => {
                  const Icon = ct.icon;
                  const active = contactType === ct.key;
                  return (
                    <button key={ct.key} type="button" onClick={() => setContactType(ct.key)}
                      style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:'6px', padding:'10px',
                        borderRadius:'10px', border: active ? `1px solid ${ac}` : `1px solid ${DS.border}`,
                        background: active ? `${ac}08` : DS.cream, color: active ? ac : DS.muted,
                        fontFamily:'DM Sans,sans-serif', fontSize:'13px', fontWeight:500, cursor:'pointer', transition:'all 200ms' }}>
                      <Icon style={{ width:'14px', height:'14px' }} /> {ct.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <label style={{ fontFamily:'DM Sans,sans-serif', fontSize:'11px', fontWeight:500, color:DS.muted,
              letterSpacing:'0.04em', textTransform:'uppercase', marginBottom:'5px', display:'block' }}>
              {mode === 'register' ? (contactType === 'email' ? 'Adresse email' : 'Numéro WhatsApp') : 'Email ou WhatsApp'}
            </label>
            <div style={{ position:'relative' }}>
              <Mail style={{ position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)', width:'15px', height:'15px', color:DS.stone }} />
              <input type="text" required value={contact} onChange={e => setContact(e.target.value)}
                placeholder={contactType === 'email' || mode === 'login' ? 'marie@exemple.com' : '+33 6 12 34 56 78'}
                data-testid="auth-modal-contact"
                style={{ width:'100%', boxSizing:'border-box', padding:'11px 14px 11px 38px', borderRadius:'10px',
                  border:`1px solid ${DS.border}`, background:DS.cream, fontFamily:'DM Sans,sans-serif', fontSize:'14px', color:DS.ink, outline:'none' }} />
            </div>
          </div>
          <div>
            <label style={{ fontFamily:'DM Sans,sans-serif', fontSize:'11px', fontWeight:500, color:DS.muted,
              letterSpacing:'0.04em', textTransform:'uppercase', marginBottom:'5px', display:'block' }}>Mot de passe</label>
            <div style={{ position:'relative' }}>
              <Lock style={{ position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)', width:'15px', height:'15px', color:DS.stone }} />
              <input type={showPwd ? 'text' : 'password'} required minLength={4} value={password} onChange={e => setPassword(e.target.value)}
                placeholder="••••••••" data-testid="auth-modal-password"
                style={{ width:'100%', boxSizing:'border-box', padding:'11px 42px 11px 38px', borderRadius:'10px',
                  border:`1px solid ${DS.border}`, background:DS.cream, fontFamily:'DM Sans,sans-serif', fontSize:'14px', color:DS.ink, outline:'none' }} />
              <button type="button" onClick={() => setShowPwd(!showPwd)}
                style={{ position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', padding:0, color:DS.stone }}>
                {showPwd ? <EyeOff style={{ width:'15px', height:'15px' }} /> : <Eye style={{ width:'15px', height:'15px' }} />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={loading} data-testid="auth-modal-submit"
            style={{ width:'100%', padding:'13px', borderRadius:'10px', border:'none', cursor:'pointer',
              background:ac, color:'#fff', fontFamily:'DM Sans,sans-serif', fontSize:'14px', fontWeight:600,
              opacity: loading ? 0.5 : 1, transition:'opacity 150ms', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
            {loading ? <Loader2 style={{ width:'16px', height:'16px', animation:'caSpin 0.8s linear infinite' }} /> :
              mode === 'register' ? 'Créer mon compte' : 'Me connecter'}
          </button>
        </form>
      </div>
      <style>{`@keyframes caSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
